/**
 * Inline financial report renderer.
 *
 * Receives a structured P&L / Balance Sheet payload from the financial-report
 * agent and renders it as Notion-style block in the chat (with download link
 * if the agent generated a PDF/Excel).
 *
 * Phase 3 — Renders any of:
 *   - PNL  (revenue / direct expenses / indirect expenses / net profit)
 *   - BS   (assets / liabilities / equity)
 *   - SCH3 (Schedule III companies-act format — flat sections)
 *
 * Server returns this in tool_call.result.report.
 */

import { Download, FileText } from 'lucide-react';
import clsx from 'clsx';

export interface FinancialReportRow {
  label: string;
  amount?: number;
  bold?: boolean;
  indent?: number; // 0 = top group, 1 = group, 2 = leaf
}

export interface FinancialReport {
  kind: 'PNL' | 'BS' | 'SCH3' | 'OTHER';
  title: string;
  period: string;
  company: string;
  sections: Array<{
    heading: string;
    rows: FinancialReportRow[];
    subtotal?: number;
  }>;
  total?: { label: string; amount: number };
  download_url?: string;
  notes?: string[];
}

interface FinancialReportCardProps {
  report: FinancialReport;
}

export function FinancialReportCard({ report }: FinancialReportCardProps) {
  return (
    <div className="border border-border rounded-md bg-card overflow-hidden my-2">
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="w-4 h-4" />
              {report.title}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {report.company} · {report.period}
            </div>
          </div>
          {report.download_url && (
            <a
              href={report.download_url}
              target="_blank"
              rel="noopener"
              download
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Download className="w-3 h-3" />
              Download
            </a>
          )}
        </div>
      </div>

      <div className="divide-y divide-border">
        {report.sections.map((section, i) => (
          <div key={i} className="px-4 py-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
              {section.heading}
            </div>
            <table className="w-full text-sm">
              <tbody>
                {section.rows.map((row, j) => (
                  <tr key={j} className={clsx(row.bold && 'font-medium')}>
                    <td
                      className="py-0.5"
                      style={{ paddingLeft: `${(row.indent ?? 0) * 16}px` }}
                    >
                      {row.label}
                    </td>
                    <td className="py-0.5 text-right font-mono whitespace-nowrap">
                      {row.amount != null ? formatAmount(row.amount) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              {section.subtotal != null && (
                <tfoot>
                  <tr className="border-t border-border font-semibold">
                    <td className="pt-1">Subtotal</td>
                    <td className="pt-1 text-right font-mono">
                      {formatAmount(section.subtotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ))}
      </div>

      {report.total && (
        <div className="px-4 py-2 border-t-2 border-foreground/20 bg-muted/30">
          <div className="flex items-center justify-between font-bold">
            <span className="text-sm">{report.total.label}</span>
            <span className="font-mono">{formatAmount(report.total.amount)}</span>
          </div>
        </div>
      )}

      {report.notes && report.notes.length > 0 && (
        <div className="px-4 py-2 border-t border-border bg-muted/10 text-xs text-muted-foreground space-y-1">
          {report.notes.map((n, i) => (
            <div key={i}>• {n}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatAmount(n: number): string {
  // Indian rupee format with lakhs/crores grouping (₹1,23,45,678.90)
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const parts = abs.toFixed(2).split('.');
  let intPart = parts[0];
  // Apply Indian grouping (last 3 digits then groups of 2)
  if (intPart.length > 3) {
    const last3 = intPart.slice(-3);
    const rest = intPart.slice(0, -3);
    const grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    intPart = grouped + ',' + last3;
  }
  return `${sign}₹${intPart}.${parts[1]}`;
}
